/**
 * Invoices tab — MONEY ONLY. The whole tab is hidden when !sees_money (the 360 screen never even adds
 * it to the tab strip), and the data hook is gated on canSeeMoney so a Viewer never fires the request
 * the server would 403. The list is the UNIFIED history (recent api + historical pdf archive); a row
 * opens a detail modal with the line items and View / Download PDF actions (the PDF is fetched as an
 * auth-bearing blob; it 404s gracefully when the archive file isn't present in this environment).
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FileText, Download, ExternalLink, X } from "lucide-react";
import {
  Table,
  type Column,
  Button,
  Modal,
  Badge,
  StatusPill,
  Input,
  Select,
  DateRangePicker,
  type DateRange,
  ErrorState,
  EmptyState,
  SkeletonRows,
} from "@/components/ui";
import { api, ApiRequestError } from "@/api/client";
import { formatDate, formatInt, formatMoney } from "@/lib/format";
import { useRbac } from "@/context/RbacContext";
import type { InvoiceItem, InvoiceRow } from "@/api/types";
import { asTKey } from "@/i18n/keys";
import { Section, statusLabel, statusTone } from "../display";
import { useCustomerInvoices, useCustomerInvoiceDetail } from "../useCustomer";

type SearchField = "invoice" | "po";

export function InvoicesTab({ accountId }: { accountId: string }) {
  const { t } = useTranslation();
  const { canSeeMoney } = useRbac();
  const invoices = useCustomerInvoices(accountId, true);
  const [openNumber, setOpenNumber] = useState<string | null>(null);

  // Filter state (all hooks BEFORE the early returns). Exact-match on Invoice/PO number + a date range
  // that DEFAULTS to the last 12 months (per the principal's spec).
  const [field, setField] = useState<SearchField>("invoice");
  const [query, setQuery] = useState("");
  const defaultRange = useMemo<DateRange>(() => {
    const end = new Date();
    const start = new Date();
    start.setFullYear(end.getFullYear() - 1);
    const iso = (d: Date) => d.toISOString().slice(0, 10);
    return { start: iso(start), end: iso(end) };
  }, []);
  const [dateRange, setDateRange] = useState<DateRange | null>(defaultRange);

  const allRows = useMemo(() => invoices.data?.invoices ?? [], [invoices.data]);
  const bounds = useMemo<DateRange>(() => {
    const ds = allRows
      .map((i) => i.invoice_date)
      .filter((d): d is string => !!d)
      .map((d) => d.slice(0, 10))
      .sort();
    const lo = ds[0];
    const hi = ds[ds.length - 1];
    return lo !== undefined && hi !== undefined
      ? { start: lo, end: hi }
      : { start: "2015-01-01", end: "2099-12-31" };
  }, [allRows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allRows.filter((r) => {
      if (q) {
        const v = (field === "invoice" ? r.invoice_number : r.po_number) ?? "";
        if (v.trim().toLowerCase() !== q) return false; // EXACT match
      }
      if (dateRange) {
        const d = r.invoice_date?.slice(0, 10);
        // A null-date invoice can't be placed in a window → never hidden by the date range (only rows
        // that HAVE a date outside the range are filtered out).
        if (d && (d < dateRange.start || d > dateRange.end)) return false;
      }
      return true;
    });
  }, [allRows, field, query, dateRange]);

  // Defence in depth: should never render for a non-money role (tab is hidden), but never leak if it does.
  if (!canSeeMoney()) {
    return <ErrorState title={t("state.forbiddenTitle")} body={t("customer.money.hidden")} />;
  }

  if (invoices.isPending) return <SkeletonRows rows={5} />;
  if (invoices.isError)
    return <ErrorState error={invoices.error} onRetry={() => void invoices.refetch()} />;

  // A date window is itself a filter (the default 12-month window counts), so Reset is offered whenever
  // one is applied. "Reset" clears to ALL invoices (no window) — the one-click "show everything" path,
  // distinct from the last-12-months DEFAULT applied on first load.
  const hasFilter = query.trim() !== "" || dateRange != null;
  function reset() {
    setQuery("");
    setField("invoice");
    setDateRange(null);
  }

  const columns: Column<InvoiceRow>[] = [
    {
      key: "invoice_number",
      header: t("customer.invoices.number"),
      cell: (r) => <span className="tabular font-medium text-(--text)">{r.invoice_number}</span>,
      sortValue: (r) => r.invoice_number,
    },
    {
      key: "po_number",
      header: t("customer.invoices.poNumber"),
      cell: (r) => <span className="tabular text-(--text)">{r.po_number ?? "—"}</span>,
      sortValue: (r) => r.po_number,
    },
    {
      key: "invoice_date",
      header: t("customer.invoices.date"),
      cell: (r) => formatDate(r.invoice_date),
      sortValue: (r) => r.invoice_date,
    },
    {
      key: "total_amount",
      header: t("customer.invoices.amount"),
      cell: (r) => formatMoney(r.total_amount, r.currency) ?? "—",
      // amount is a JSON string → coerce so sort is numeric; keep null as null so the Table's
      // nulls-last guard still groups "—" rows at the bottom (Number(null)=0 would intermix them with $0).
      sortValue: (r) =>
        r.total_amount == null || r.total_amount === "" ? null : Number(r.total_amount),
      align: "right",
      numeric: true,
    },
    {
      key: "status",
      header: t("customer.status"),
      cell: (r) =>
        r.status ? (
          <StatusPill tone={statusTone(r.status)}>{statusLabel(t, r.status)}</StatusPill>
        ) : (
          "—"
        ),
      sortValue: (r) => r.status,
    },
    {
      key: "source",
      header: t("customer.invoices.source"),
      cell: (r) => {
        // Note the joiner is "_", not "." — these are flat sibling keys (source_pdf, source_api …),
        // so the prefix passed to asTKey carries it. An unknown source keeps the old raw-token fallback.
        const key = asTKey("customer.invoices.source_", r.source ?? "unknown");
        return (
          <Badge tone={r.source === "pdf" ? "neutral" : "brand"}>
            {key ? t(key) : (r.source ?? "—")}
          </Badge>
        );
      },
      sortValue: (r) => r.source,
    },
    {
      key: "pdf",
      header: t("customer.invoices.pdf"),
      cell: (r) =>
        r.has_pdf ? (
          <FileText
            className="size-4 text-(--text-muted)"
            aria-label={t("customer.invoices.hasPdf")}
          />
        ) : (
          <span className="text-(--text-subtle)">—</span>
        ),
    },
  ];

  return (
    <Section title={t("customer.money.invoices")}>
      {/* Filter bar — exact-match Invoice/PO number + a date range that DEFAULTS to the last 12 months. */}
      <div className="flex flex-wrap items-end gap-2.5">
        <div className="flex items-end gap-0">
          <Select
            value={field}
            onChange={(v) => setField(v as SearchField)}
            options={[
              { value: "invoice", label: t("customer.invoices.searchInvoice") },
              { value: "po", label: t("customer.invoices.searchPo") },
            ]}
            aria-label={t("customer.invoices.searchBy")}
            className="w-44 [&>button]:rounded-r-none [&>button]:border-r-0"
          />
          <Input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("customer.invoices.searchPlaceholder")}
            aria-label={t("customer.invoices.searchPlaceholder")}
            autoComplete="off"
            spellCheck={false}
            className="h-9 w-48 rounded-l-none"
          />
        </div>
        <DateRangePicker
          value={dateRange ?? bounds}
          onChange={setDateRange}
          min={bounds.start < defaultRange.start ? bounds.start : defaultRange.start}
          max={bounds.end > defaultRange.end ? bounds.end : defaultRange.end}
        />
        {hasFilter && (
          <Button variant="ghost" size="sm" onClick={reset}>
            <X className="size-4" aria-hidden />
            {t("customer.invoices.reset")}
          </Button>
        )}
        <span className="tabular ml-auto self-center text-(length:--text-xs) text-(--text-muted)">
          {t("customer.users.count", {
            shown: formatInt(filtered.length),
            total: formatInt(allRows.length),
          })}
        </span>
      </div>

      <Table
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.invoice_id}
        caption={t("customer.money.invoices")}
        initialSort={{ key: "invoice_date", dir: "desc" }}
        onRowClick={(r) => setOpenNumber(r.invoice_number)}
        empty={
          // Drive the empty-state off DATA PRESENCE, not hasFilter: an account WITH invoices whose rows
          // all fall outside the default 12-month window must read "no match" (widen/reset) — never the
          // false "This account has no invoices". Only a truly invoice-less account shows the latter.
          allRows.length > 0 ? (
            <EmptyState
              title={t("customer.invoices.noMatch")}
              body={t("customer.invoices.noMatchBody")}
            />
          ) : (
            <EmptyState
              title={t("customer.invoices.empty")}
              body={t("customer.invoices.emptyBody")}
            />
          )
        }
      />

      <InvoiceDetailModal
        accountId={accountId}
        invoiceNumber={openNumber}
        onClose={() => setOpenNumber(null)}
      />
    </Section>
  );
}

function InvoiceDetailModal({
  accountId,
  invoiceNumber,
  onClose,
}: {
  accountId: string;
  invoiceNumber: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detail = useCustomerInvoiceDetail(accountId, invoiceNumber);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  async function handlePdf(num: string, mode: "view" | "download") {
    setPdfBusy(true);
    setPdfError(null);
    // For "view", open the tab SYNCHRONOUSLY (within the click's user-activation) and navigate it after
    // the fetch — opening after the await is popup-blocked by Safari (activation doesn't survive await).
    // Do NOT pass "noopener": that makes window.open RETURN null (the tab still opens), losing the handle
    // we need to navigate/close. Keep the handle and sever the opener manually for the same security.
    const viewTab = mode === "view" ? window.open("", "_blank") : null;
    if (viewTab) viewTab.opener = null;
    try {
      const blob = await api.customerInvoicePdf(accountId, num);
      const url = URL.createObjectURL(blob);
      if (mode === "view") {
        if (viewTab) viewTab.location.href = url;
        else window.open(url, "_blank", "noopener"); // popup blocked → best-effort retry
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${num}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke after a beat so the new tab/download has consumed it.
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      if (viewTab) viewTab.close(); // failed fetch → don't leave a blank tab open
      const msg =
        e instanceof ApiRequestError && e.error.status === 404
          ? t("customer.invoices.pdfUnavailable")
          : t("customer.invoices.pdfError");
      setPdfError(msg);
    } finally {
      setPdfBusy(false);
    }
  }

  const inv = detail.data?.invoice;
  const hasPdf = inv?.has_pdf ?? false;

  return (
    <Modal
      open={invoiceNumber !== null}
      onClose={() => {
        setPdfError(null);
        onClose();
      }}
      title={
        invoiceNumber
          ? t("customer.invoices.detailTitle", { number: invoiceNumber })
          : t("customer.money.invoices")
      }
      size="lg"
      footer={
        invoiceNumber && hasPdf ? (
          <div className="flex w-full items-center justify-between gap-3">
            <span className="text-(length:--text-xs) text-(--danger)">{pdfError}</span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pdfBusy}
                onClick={() => void handlePdf(invoiceNumber, "view")}
              >
                <ExternalLink className="size-4" aria-hidden />
                {t("customer.invoices.viewPdf")}
              </Button>
              <Button
                size="sm"
                disabled={pdfBusy}
                onClick={() => void handlePdf(invoiceNumber, "download")}
              >
                <Download className="size-4" aria-hidden />
                {t("customer.invoices.downloadPdf")}
              </Button>
            </div>
          </div>
        ) : (
          pdfError && <span className="text-(length:--text-xs) text-(--danger)">{pdfError}</span>
        )
      }
    >
      {detail.isPending ? (
        <SkeletonRows rows={4} />
      ) : detail.isError ? (
        <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />
      ) : detail.data ? (
        <div className="flex flex-col gap-4">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
            <Field label={t("customer.invoices.date")} value={formatDate(inv?.invoice_date)} />
            <Field label={t("customer.invoices.dueDate")} value={formatDate(inv?.due_date)} />
            <Field
              label={t("customer.status")}
              value={
                inv?.status ? (
                  <StatusPill tone={statusTone(inv.status)}>
                    {statusLabel(t, inv.status)}
                  </StatusPill>
                ) : (
                  "—"
                )
              }
            />
            <Field
              label={t("customer.invoices.amount")}
              value={formatMoney(inv?.total_amount, inv?.currency) ?? "—"}
              mono
            />
            <Field
              label={t("customer.invoices.tax")}
              value={formatMoney(inv?.tax_amount, inv?.currency) ?? "—"}
              mono
            />
            <Field
              label={t("customer.invoices.balance")}
              value={formatMoney(inv?.balance, inv?.currency) ?? "—"}
              mono
            />
          </dl>

          <ItemsTable items={detail.data.items} currency={inv?.currency ?? null} />
        </div>
      ) : null}
    </Modal>
  );
}

function Field({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-(length:--text-2xs) font-medium text-(--text-muted)">{label}</dt>
      <dd
        className={
          mono
            ? "tabular text-(length:--text-sm) font-medium text-(--text-strong)"
            : "text-(length:--text-sm) text-(--text)"
        }
      >
        {value}
      </dd>
    </div>
  );
}

type KeyedItem = InvoiceItem & { _idx: number };

/**
 * Unit price = the mirrored value when present, else derived from line total / quantity. Zoom's invoice
 * (and the PDF-extraction backfill) leave per-line unit_price NULL on many rows while quantity + line
 * total are present, so a real per-unit price is recoverable (e.g. qty 10 / total 1,540 → 154.00).
 * Returns null (→ "—") when quantity is absent/zero or the total is missing — never fabricate or /0.
 */
function deriveUnitPrice(item: InvoiceItem): number | string | null {
  if (item.unit_price != null && item.unit_price !== "") return item.unit_price;
  const qty = item.quantity == null || item.quantity === "" ? NaN : Number(item.quantity);
  const total =
    item.total_amount == null || item.total_amount === "" ? NaN : Number(item.total_amount);
  if (!Number.isFinite(qty) || qty === 0 || !Number.isFinite(total)) return null;
  return total / qty;
}

function ItemsTable({ items, currency }: { items: InvoiceItem[]; currency: string | null }) {
  const { t } = useTranslation();
  if (items.length === 0) {
    return <EmptyState title={t("customer.invoices.noItems")} />;
  }
  const rows: KeyedItem[] = items.map((it, i) => ({ ...it, _idx: i }));
  const columns: Column<KeyedItem>[] = [
    {
      key: "charge_name",
      header: t("customer.invoices.charge"),
      cell: (r) => <span className="text-(--text)">{r.charge_name ?? "—"}</span>,
    },
    {
      key: "charge_type",
      header: t("customer.invoices.chargeType"),
      cell: (r) => r.charge_type ?? "—",
    },
    {
      key: "quantity",
      header: t("customer.invoices.qty"),
      cell: (r) => (r.quantity == null ? "—" : formatInt(Number(r.quantity))),
      align: "right",
      numeric: true,
    },
    {
      key: "unit_price",
      header: t("customer.invoices.unitPrice"),
      cell: (r) => formatMoney(deriveUnitPrice(r), currency) ?? "—",
      align: "right",
      numeric: true,
    },
    {
      key: "total_amount",
      header: t("customer.invoices.lineTotal"),
      cell: (r) => formatMoney(r.total_amount, currency) ?? "—",
      align: "right",
      numeric: true,
    },
  ];
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-(length:--text-sm) font-semibold text-(--text-strong)">
        {t("customer.invoices.lineItems")}
      </h3>
      <Table
        columns={columns}
        rows={rows}
        rowKey={(r) => String(r._idx)}
        caption={t("customer.invoices.lineItems")}
        paginated={false}
      />
    </div>
  );
}
